import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  FiArrowLeft,
  FiSave,
  FiCheck,
  FiAlertCircle,
  FiCpu,
  FiArrowRight,
  FiLayers,
  FiCheckCircle,
  FiRefreshCw,
  FiCalendar,
  FiInfo,
} from "react-icons/fi";
import { useAuth } from "../../../services/useAuth";
import { hasPageWriteAccess } from "../../../config/roles";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Select from "../../../components/BaseUI/Select";
import Lozenge from "../../../components/BaseUI/Lozenge";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import { withAuth } from "../../../components/HOC/withAuth";
import styles from "./WorkOrders.module.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const PRIORITY_OPTIONS = [
  { value: "urgent", label: "Acil (En Yüksek Öncelik)" },
  { value: "high", label: "Yüksek Öncelik" },
  { value: "normal", label: "Normal Öncelik" },
  { value: "low", label: "Düşük Öncelik" },
];

const STATUS_OPTIONS = [
  { value: "draft", label: "Taslak" },
  { value: "planned", label: "Planlandı" },
  { value: "in_progress", label: "Üretimde" },
  { value: "paused", label: "Durduruldu" },
  { value: "completed", label: "Tamamlandı" },
  { value: "cancelled", label: "İptal Edildi" },
];

const extractVal = (val) => {
  if (val === null || val === undefined) return "";
  if (typeof val === "object") {
    if (val.target && val.target.value !== undefined) return val.target.value;
    if (val.value !== undefined) return val.value;
  }
  return String(val);
};

const WorkOrderFormPage = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  useTitle(
    isEdit
      ? "Rovion MES - İş Emri Düzenle"
      : "Rovion MES - Yeni İş Emri Oluştur",
  );

  const { getToken, getUser } = useAuth();
  const currentUser = getUser ? getUser() : null;
  const canWrite =
    !!currentUser &&
    hasPageWriteAccess(
      currentUser.role,
      "/work-orders",
      currentUser.pages || [],
      !!currentUser.fullAccess,
    );

  useEffect(() => {
    if (currentUser && !canWrite) {
      navigate("/work-orders");
    }
  }, [currentUser, canWrite, navigate]);

  const authHeaders = useCallback(() => {
    const token = getToken();
    return { Authorization: `Bearer ${token}` };
  }, [getToken]);

  // Toast notifications
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((message, type = "info") => {
    const t = { id: Date.now(), message, type };
    setToasts((prev) => [...prev, t]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== t.id));
    }, 3500);
  }, []);

  // Lookups
  const [deviceModels, setDeviceModels] = useState([]);
  const [parts, setParts] = useState([]);
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [allMachines, setAllMachines] = useState([]);

  // Form Fields
  const [type, setType] = useState("model"); // "model" | "part"
  const [selectedModelId, setSelectedModelId] = useState("");
  const [selectedPartId, setSelectedPartId] = useState("");
  const [selectedSaleId, setSelectedSaleId] = useState("");
  const [workOrderNumber, setWorkOrderNumber] = useState("");
  const [title, setTitle] = useState("");
  const [plannedQuantity, setPlannedQuantity] = useState("100");
  const [priority, setPriority] = useState("normal");
  const [status, setStatus] = useState("planned");
  const [autoStart, setAutoStart] = useState(true);
  const [plannedStartDate, setPlannedStartDate] = useState(() => {
    const d = new Date();
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [plannedEndDate, setPlannedEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    d.setMinutes(0, 0, 0);
    return d.toISOString().slice(0, 16);
  });
  const [description, setDescription] = useState("");
  const [instructions, setInstructions] = useState("");

  // Pipeline flow state
  const [flowLoading, setFlowLoading] = useState(false);
  const [flowStages, setFlowStages] = useState([]);
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [saving, setSaving] = useState(false);

  // Fetch Lookups
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const headers = authHeaders();
        const myCompId = String(currentUser?.companyId || currentUser?.company?.id || "");
        const [modelsRes, partsRes, salesRes, usersRes, machinesRes] =
          await Promise.all([
            axios
              .get(`${API_URL}/api/device-models`, { headers })
              .catch(() => ({ data: [] })),
            axios
              .get(`${API_URL}/api/parts`, { headers })
              .catch(() => ({ data: [] })),
            axios
              .get(`${API_URL}/api/device-sales`, { headers })
              .catch(() => ({ data: [] })),
            axios
              .get(`${API_URL}/api/users`, {
                headers,
                params: myCompId ? { companyId: myCompId } : undefined,
              })
              .catch(() => ({ data: [] })),
            axios
              .get(`${API_URL}/api/machines`, { headers })
              .catch(() => ({ data: [] })),
          ]);

        const mList =
          modelsRes.data?.models ||
          modelsRes.data?.deviceModels ||
          modelsRes.data ||
          [];
        setDeviceModels(Array.isArray(mList) ? mList : []);

        const pList = partsRes.data?.parts || partsRes.data?.data || [];
        setParts(Array.isArray(pList) ? pList : []);

        const sList = salesRes.data?.sales || salesRes.data?.data || [];
        setSales(Array.isArray(sList) ? sList : []);

        const rawUsers = usersRes.data?.users || usersRes.data?.data || [];
        const scopedUsers = myCompId
          ? rawUsers.filter((u) => {
              const uComp = String(u?.companyId || u?.company?.id || u?.tenant || "");
              return !uComp || uComp === myCompId;
            })
          : rawUsers;
        setUsers(Array.isArray(scopedUsers) ? scopedUsers : []);

        const macList = machinesRes.data?.machines || machinesRes.data || [];
        setAllMachines(Array.isArray(macList) ? macList : []);
      } catch (err) {
        console.error("Lookups fetch error:", err);
      }
    };

    fetchLookups();
  }, [authHeaders, currentUser?.companyId, currentUser?.company?.id]);

  // Load Work Order if Edit Mode
  useEffect(() => {
    if (!isEdit) {
      setLoadingInitial(false);
      return;
    }

    const fetchOrder = async () => {
      setLoadingInitial(true);
      try {
        const headers = authHeaders();
        const res = await axios.get(`${API_URL}/api/work-orders/${id}`, {
          headers,
        });
        const order = res.data?.data;
        if (!order) {
          addToast("İş emri bulunamadı.", "error");
          navigate("/work-orders");
          return;
        }

        setWorkOrderNumber(order.workOrderNumber || "");
        setTitle(order.title || "");
        setType(order.type || "model");
        setSelectedModelId(
          order.deviceModelId?._id || order.deviceModelId || "",
        );
        setSelectedPartId(order.partId?._id || order.partId || "");
        setSelectedSaleId(order.saleId?._id || order.saleId || "");
        setPlannedQuantity(String(order.plannedQuantity || 100));
        setPriority(order.priority || "normal");
        setStatus(order.status || "planned");
        setAutoStart(order.autoStart !== false);

        if (order.plannedStartDate) {
          setPlannedStartDate(
            new Date(order.plannedStartDate).toISOString().slice(0, 16),
          );
        }
        if (order.plannedEndDate) {
          setPlannedEndDate(
            new Date(order.plannedEndDate).toISOString().slice(0, 16),
          );
        }

        setDescription(order.description || "");
        setInstructions(order.instructions || "");

        // Set stages
        if (Array.isArray(order.stages)) {
          const orderQty = Number(order.plannedQuantity) || 1;
          const finalStage = order.stages[order.stages.length - 1];
          const inferredBaseQty =
            Number(finalStage?.plannedQuantity) || orderQty;

          setFlowStages(
            order.stages.map((st) => {
              const ratio =
                Number(st.consumptionRatio) ||
                (inferredBaseQty > 0
                  ? Math.round(
                      (Number(st.plannedQuantity) / inferredBaseQty) * 100,
                    ) / 100
                  : 1) ||
                1;

              const stagePlanned =
                Number(st.plannedQuantity) && inferredBaseQty === orderQty
                  ? Number(st.plannedQuantity)
                  : Math.round(orderQty * ratio);

              return {
                ...st,
                consumptionRatio: ratio,
                plannedQuantity: stagePlanned,
                currentStock: Number(st.currentStock) || 0,
                partId: st.partId?._id || st.partId,
                isFulfilledFromStock: !!st.isFulfilledFromStock,
                selectedMachineId: String(st.machineId?._id || st.machineId || ""),
                selectedOperatorId: String(
                  st.assignedOperators?.[0]?._id ||
                  st.assignedOperators?.[0] ||
                  "",
                ),
              };
            }),
          );
        }
      } catch (err) {
        console.error("Fetch work order error:", err);
        addToast(
          err?.response?.data?.message || "İş emri yüklenemedi.",
          "error",
        );
      } finally {
        setLoadingInitial(false);
      }
    };

    fetchOrder();
  }, [id, isEdit, authHeaders, addToast, navigate]);

  // Handle Sale selection (auto-populate model and count)
  const handleSaleChange = (saleId) => {
    setSelectedSaleId(saleId);
    if (!saleId) return;
    const sale = sales.find((s) => String(s._id) === String(saleId));
    if (sale) {
      if (sale.models && sale.models.length > 0) {
        const firstModel = sale.models[0];
        const mId = firstModel.modelId?._id || firstModel.modelId;
        if (mId) {
          setType("model");
          setSelectedModelId(String(mId));
          if (firstModel.count)
            handlePlannedQuantityChange(String(firstModel.count));
        }
      }
      if (sale.terminTarihi) {
        setPlannedEndDate(
          new Date(sale.terminTarihi).toISOString().slice(0, 16),
        );
      }
    }
  };

  // Handle planned quantity change and scale all stage quantities proportionally
  const handlePlannedQuantityChange = (val) => {
    const rawVal = extractVal(val);
    setPlannedQuantity(rawVal);
    const newQty = Number(rawVal);
    if (!newQty || newQty <= 0) return;

    setFlowStages((prevStages) =>
      prevStages.map((stage) => {
        const ratio = Number(stage.consumptionRatio) || 1;
        return {
          ...stage,
          plannedQuantity: Math.round(newQty * ratio),
        };
      }),
    );
  };

  // Resolve pipeline flow from backend
  const resolveFlow = useCallback(async () => {
    const targetId = type === "model" ? selectedModelId : selectedPartId;
    if (!targetId) return;

    setFlowLoading(true);
    try {
      const payload = {
        quantity: Number(plannedQuantity) || 1,
      };
      if (type === "model") payload.deviceModelId = targetId;
      else payload.partId = targetId;

      const res = await axios.post(
        `${API_URL}/api/work-orders/resolve-flow`,
        payload,
        { headers: authHeaders() },
      );

      const resolvedStages = res.data?.data?.stages || [];
      setFlowStages((prev) => {
        if (prev.length > 0) {
          const prevMap = new Map(
            prev.map((s) => [String(s.partId?._id || s.partId), s]),
          );
          return resolvedStages.map((st) => {
            const old = prevMap.get(String(st.partId));
            return {
              ...st,
              consumptionRatio: Number(st.consumptionRatio) || 1,
              isFulfilledFromStock: old ? !!old.isFulfilledFromStock : false,
              selectedMachineId:
                old?.selectedMachineId ||
                st.machineId ||
                (st.availableMachines?.[0]?._id ?? ""),
              selectedOperatorId: old?.selectedOperatorId || "",
            };
          });
        }
        return resolvedStages.map((st) => ({
          ...st,
          consumptionRatio: Number(st.consumptionRatio) || 1,
          isFulfilledFromStock: false,
          selectedMachineId:
            st.machineId || (st.availableMachines?.[0]?._id ?? ""),
          selectedOperatorId: "",
        }));
      });
    } catch (err) {
      console.error("Resolve flow error:", err);
      addToast(err?.response?.data?.message || "Akış çözümlenemedi.", "error");
    } finally {
      setFlowLoading(false);
    }
  }, [
    type,
    selectedModelId,
    selectedPartId,
    plannedQuantity,
    authHeaders,
    addToast,
  ]);

  // Auto-resolve flow when target changes in Create mode
  useEffect(() => {
    if (isEdit) return;
    const targetId = type === "model" ? selectedModelId : selectedPartId;
    if (targetId && Number(plannedQuantity) > 0) {
      resolveFlow();
    } else {
      setFlowStages([]);
    }
  }, [isEdit, type, selectedModelId, selectedPartId, resolveFlow]);

  // Toggle "Stoktan Karşıla"
  const handleStockToggle = (idx) => {
    setFlowStages((prev) =>
      prev.map((stage, i) => {
        if (i !== idx) return stage;
        const nextFulfilled = !stage.isFulfilledFromStock;
        let machineId = stage.selectedMachineId;
        if (
          !nextFulfilled &&
          !machineId &&
          stage.availableMachines?.length > 0
        ) {
          machineId =
            stage.availableMachines[0]._id || stage.availableMachines[0];
        }
        return {
          ...stage,
          isFulfilledFromStock: nextFulfilled,
          selectedMachineId: machineId,
        };
      }),
    );
  };

  // Change selected machine for stage
  const handleStageMachineChange = (idx, machineId) => {
    setFlowStages((prev) =>
      prev.map((stage, i) =>
        i === idx ? { ...stage, selectedMachineId: machineId } : stage,
      ),
    );
  };

  // Change selected operator for stage
  const handleStageOperatorChange = (idx, operatorId) => {
    const cleanId = String(operatorId?.value ?? operatorId ?? "");
    setFlowStages((prev) =>
      prev.map((stage, i) =>
        i === idx ? { ...stage, selectedOperatorId: cleanId } : stage,
      ),
    );
  };

  const operatorOptions = useMemo(
    () => [
      { value: "", label: "Operatör Seçilmedi" },
      ...[...users.filter(Boolean)]
        .sort((a, b) => {
          const aOp =
            !!a?.isOperator ||
            !!a?.profileId?.isOperator ||
            a?.role === "employee";
          const bOp =
            !!b?.isOperator ||
            !!b?.profileId?.isOperator ||
            b?.role === "employee";
          if (aOp && !bOp) return -1;
          if (!aOp && bOp) return 1;
          return (a?.name || "").localeCompare(b?.name || "");
        })
        .map((u) => {
          const isOp =
            !!u?.isOperator ||
            !!u?.profileId?.isOperator ||
            u?.role === "employee";
          return {
            value: String(u?._id || ""),
            label: `${u?.name || "Kullanıcı"}${isOp ? " [Operatör]" : ""} (${u?.email || "-"})`,
          };
        }),
    ],
    [users],
  );

  // Form Submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isEdit) {
      const targetId = type === "model" ? selectedModelId : selectedPartId;
      if (!targetId) {
        addToast(
          type === "model"
            ? "Lütfen ürün modeli seçin."
            : "Lütfen parça seçin.",
          "error",
        );
        return;
      }
    }

    if (!plannedQuantity || Number(plannedQuantity) <= 0) {
      addToast("Geçerli bir planlanan miktar giriniz.", "error");
      return;
    }

    // Validate machine selection
    for (const st of flowStages) {
      if (!st.isFulfilledFromStock && !st.selectedMachineId) {
        addToast(`'${st.partName}' aşaması için makine atanmalıdır.`, "error");
        return;
      }
    }

    setSaving(true);
    try {
      const payloadStages = flowStages.map((st, i) => ({
        stageOrder: i + 1,
        partId: st.partId?._id || st.partId,
        partName: st.partName,
        partCode: st.partCode,
        plannedQuantity:
          Number(st.plannedQuantity) ||
          Math.round(
            Number(plannedQuantity) * (Number(st.consumptionRatio) || 1),
          ),
        consumptionRatio: Number(st.consumptionRatio) || 1,
        producedQuantity: Number(st.producedQuantity) || 0,
        scrapQuantity: Number(st.scrapQuantity) || 0,
        machineId: st.isFulfilledFromStock ? null : st.selectedMachineId,
        availableMachines:
          st.availableMachines?.map((m) => m._id || m) ||
          (st.selectedMachineId ? [st.selectedMachineId] : []),
        requiresMachineSelection: st.requiresMachineSelection,
        assignedOperators: st.selectedOperatorId ? [st.selectedOperatorId] : [],
        isFulfilledFromStock: st.isFulfilledFromStock,
        cycleTimeSeconds: st.cycleTimeSeconds || 0,
        status: st.status || "pending",
      }));

      if (isEdit) {
        const updatePayload = {
          title: title || undefined,
          plannedQuantity: Number(plannedQuantity),
          priority,
          status,
          autoStart,
          plannedStartDate,
          plannedEndDate,
          description,
          instructions,
          stages: payloadStages,
        };

        const res = await axios.put(
          `${API_URL}/api/work-orders/${id}`,
          updatePayload,
          {
            headers: authHeaders(),
          },
        );

        addToast(
          res.data?.message || "İş emri başarıyla güncellendi.",
          "success",
        );
        setTimeout(() => navigate("/work-orders"), 600);
      } else {
        const createPayload = {
          type,
          plannedQuantity: Number(plannedQuantity),
          priority,
          autoStart,
          plannedStartDate,
          plannedEndDate,
          description,
          instructions,
          saleId: selectedSaleId || null,
          stages: payloadStages,
        };

        if (type === "model") createPayload.deviceModelId = selectedModelId;
        else createPayload.partId = selectedPartId;

        const res = await axios.post(
          `${API_URL}/api/work-orders`,
          createPayload,
          {
            headers: authHeaders(),
          },
        );

        addToast(
          res.data?.message || "İş emri başarıyla oluşturuldu.",
          "success",
        );
        setTimeout(() => navigate("/work-orders"), 600);
      }
    } catch (err) {
      console.error("Work order save error:", err);
      addToast(
        err?.response?.data?.message || "İş emri kaydedilemedi.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedModel = deviceModels.find(
    (m) => String(m._id) === String(selectedModelId),
  );
  const selectedPart = parts.find(
    (p) => String(p._id) === String(selectedPartId),
  );

  if (loadingInitial) {
    return (
      <AppPage title={isEdit ? "İş Emri Düzenle" : "Yeni İş Emri Oluştur"}>
        <CardContainer
          className={styles.formCardContent}
          style={{ marginBottom: 24 }}
        >
          <div
            style={{
              padding: "48px 24px",
              textAlign: "center",
              color: "var(--m-text-subtle, #64748b)",
            }}
          >
            <FiRefreshCw
              className="spin"
              size={24}
              style={{ marginBottom: 12 }}
            />
            <div>İş emri verileri yükleniyor...</div>
          </div>
        </CardContainer>
      </AppPage>
    );
  }

  return (
    <AppPage
      title={
        isEdit ? `İş Emri Düzenle: #${workOrderNumber}` : "Yeni İş Emri Oluştur"
      }
      subTitle={
        isEdit
          ? "İş emrinin üretim parametrelerini, önceliğini, tarihlerini ve hat aşamalarını güncelleyin."
          : "Hedef ürün ve miktarı belirleyerek otomatik hat akışı oluşturun ve planlayın."
      }
      actions={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Button
            appearance="subtle"
            iconBefore={<FiArrowLeft size={16} />}
            onClick={() => navigate("/work-orders")}
          >
            Geri Dön
          </Button>
          <Button
            appearance="primary"
            iconBefore={<FiSave size={16} />}
            type="submit"
            form="work-order-form"
            isLoading={saving}
          >
            {isEdit ? "Değişiklikleri Kaydet" : "İş Emrini Oluştur"}
          </Button>
        </div>
      }
    >
      <ToastContainer>
        {toasts.map((toast) => (
          <Toast key={toast.id} type={toast.type}>
            {toast.message}
          </Toast>
        ))}
      </ToastContainer>

      <form id="work-order-form" onSubmit={handleSubmit}>
        {/* Card 1: Product / Order Target */}
        <CardContainer
          className={styles.formCardContent}
          style={{ marginBottom: 24 }}
        >
          <div className={styles.cardSectionHeader}>
            <div className={styles.cardSectionTitle}>
              <FiCpu size={18} color="var(--m-primary, #6366f1)" />
              <span>
                {isEdit
                  ? "İş Emri Hedefi ve Temel Bilgiler"
                  : "Hedef Ürün ve Sipariş Seçimi"}
              </span>
            </div>
            {isEdit && (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Lozenge appearance="info-subtle" isBold>
                  #{workOrderNumber}
                </Lozenge>
                <Lozenge
                  appearance={
                    status === "in_progress"
                      ? "warning-subtle"
                      : status === "completed"
                        ? "success-subtle"
                        : status === "paused"
                          ? "danger-subtle"
                          : "default"
                  }
                  isBold
                >
                  {STATUS_OPTIONS.find((s) => s.value === status)?.label ||
                    status}
                </Lozenge>
              </div>
            )}
          </div>

          {!isEdit ? (
            <div className={styles.formGrid}>
              <div>
                <Label>İş Emri Giriş Tipi</Label>
                <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="woType"
                      checked={type === "model"}
                      onChange={() => {
                        setType("model");
                        setSelectedPartId("");
                      }}
                    />
                    Ürün Modeli Bazlı
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: 13,
                    }}
                  >
                    <input
                      type="radio"
                      name="woType"
                      checked={type === "part"}
                      onChange={() => {
                        setType("part");
                        setSelectedModelId("");
                      }}
                    />
                    Parça Bazlı
                  </label>
                </div>
              </div>

              {type === "model" ? (
                <div>
                  <Label required>Ürün Modeli</Label>
                  <Select
                    unique
                    selected={selectedModelId ? [selectedModelId] : []}
                    onSelected={(values) => {
                      const v = values?.[0]?.value ?? values?.[0] ?? "";
                      setSelectedModelId(v);
                    }}
                    value={selectedModelId}
                    onChange={(val) => setSelectedModelId(extractVal(val))}
                    options={[
                      { value: "", label: "Model Seçiniz..." },
                      ...deviceModels.filter(Boolean).map((m) => ({
                        value: m?._id || "",
                        label: `${m?.name || "İsimsiz"} (${m?.code || "-"})`,
                      })),
                    ]}
                  />
                </div>
              ) : (
                <div>
                  <Label required>Parça</Label>
                  <Select
                    unique
                    selected={selectedPartId ? [selectedPartId] : []}
                    onSelected={(values) => {
                      const v = values?.[0]?.value ?? values?.[0] ?? "";
                      setSelectedPartId(v);
                    }}
                    value={selectedPartId}
                    onChange={(val) => setSelectedPartId(extractVal(val))}
                    options={[
                      { value: "", label: "Parça Seçiniz..." },
                      ...parts.filter(Boolean).map((p) => ({
                        value: p?._id || "",
                        label: `${p?.name || "İsimsiz"} (${p?.code || "-"})`,
                      })),
                    ]}
                  />
                </div>
              )}

              <div>
                <Label>Sipariş Bağlantısı (Opsiyonel)</Label>
                <Select
                  unique
                  selected={selectedSaleId ? [selectedSaleId] : []}
                  onSelected={(values) => {
                    const v = values?.[0]?.value ?? values?.[0] ?? "";
                    handleSaleChange(v);
                  }}
                  value={selectedSaleId}
                  onChange={(val) => handleSaleChange(extractVal(val))}
                  options={[
                    { value: "", label: "Bağımsız İş Emri (Siparişsiz)" },
                    ...sales.filter(Boolean).map((s) => ({
                      value: s?._id || "",
                      label: `${s?.saleName || s?.orderNumber || "Sipariş"} ${
                        s?.companyId?.name ? `(${s.companyId.name})` : ""
                      }`,
                    })),
                  ]}
                />
              </div>

              <div>
                <Label required>Planlanan Üretim Miktarı (Adet)</Label>
                <Input
                  type="number"
                  min="1"
                  value={plannedQuantity}
                  onChange={handlePlannedQuantityChange}
                  placeholder="100"
                />
              </div>
            </div>
          ) : (
            <div className={styles.formGrid}>
              <div>
                <Label>Ürün / Model Bilgisi</Label>
                <div
                  style={{
                    padding: "8px 12px",
                    background: "var(--m-surface-subtle, #f8fafc)",
                    border: "1px solid var(--m-border, #e2e8f0)",
                    borderRadius: 6,
                    fontWeight: 600,
                    fontSize: 13,
                    color: "var(--m-text, #1e293b)",
                  }}
                >
                  {type === "model"
                    ? selectedModel
                      ? `${selectedModel.name} (${selectedModel.code || "-"})`
                      : "Model Atanmış"
                    : selectedPart
                      ? `${selectedPart.name} (${selectedPart.code || "-"})`
                      : "Parça Atanmış"}
                </div>
              </div>

              <div>
                <Label required>Planlanan Üretim Miktarı (Adet)</Label>
                <Input
                  type="number"
                  min="1"
                  value={plannedQuantity}
                  onChange={handlePlannedQuantityChange}
                  placeholder="100"
                />
              </div>

              <div>
                <Label>İş Emri Durumu</Label>
                <Select
                  unique
                  selected={status ? [status] : []}
                  onSelected={(values) => {
                    const v = values?.[0]?.value ?? values?.[0] ?? "planned";
                    setStatus(v);
                  }}
                  value={status}
                  onChange={(val) => setStatus(extractVal(val))}
                  options={STATUS_OPTIONS}
                />
              </div>

              <div>
                <Label>Özel Başlık / Not</Label>
                <Input
                  value={title}
                  onChange={(val) => setTitle(extractVal(val))}
                  placeholder="İş emri başlığı..."
                />
              </div>
            </div>
          )}
        </CardContainer>

        {/* Card 2: Planning & Priority */}
        <CardContainer
          className={styles.formCardContent}
          style={{ marginBottom: 24 }}
        >
          <div className={styles.cardSectionHeader}>
            <div className={styles.cardSectionTitle}>
              <FiCalendar size={18} color="var(--m-primary, #6366f1)" />
              <span>Zamanlama ve Öncelik Ayarları</span>
            </div>
          </div>

          <div className={styles.formGrid}>
            <div>
              <Label>Öncelik Derecesi</Label>
              <Select
                unique
                selected={priority ? [priority] : ["normal"]}
                onSelected={(values) => {
                  const v = values?.[0]?.value ?? values?.[0] ?? "normal";
                  setPriority(v);
                }}
                value={priority}
                onChange={(val) => setPriority(extractVal(val))}
                options={PRIORITY_OPTIONS}
              />
            </div>

            <div>
              <Label>Planlanan Başlangıç Tarihi</Label>
              <Input
                type="datetime-local"
                value={plannedStartDate}
                onChange={(val) => setPlannedStartDate(extractVal(val))}
              />
            </div>

            <div>
              <Label>Planlanan Bitiş Tarihi</Label>
              <Input
                type="datetime-local"
                value={plannedEndDate}
                onChange={(val) => setPlannedEndDate(extractVal(val))}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: 20,
            }}
          >
            <div>
              <Label>Açıklama</Label>
              <Input
                value={description}
                onChange={(val) => setDescription(extractVal(val))}
                placeholder="İş emrine ilişkin genel açıklamalar..."
              />
            </div>

            <div>
              <Label>Üretim Talimatları / SOP</Label>
              <Input
                value={instructions}
                onChange={(val) => setInstructions(extractVal(val))}
                placeholder="Operatör ve hat için özel talimatlar..."
              />
            </div>
          </div>
        </CardContainer>

        {/* Card 3: Visual Pipeline Flow (Hat Şeması) */}
        <CardContainer
          className={styles.formCardContent}
          style={{ marginBottom: 24 }}
        >
          <div className={styles.cardSectionHeader}>
            <div className={styles.cardSectionTitle}>
              <FiLayers size={18} color="var(--m-primary, #6366f1)" />
              <span>Üretim Hattı ve Aşamalar (Hat Şeması)</span>
            </div>
            {flowStages.length > 0 && (
              <Button
                appearance="subtle"
                iconBefore={<FiRefreshCw size={13} />}
                onClick={resolveFlow}
                disabled={flowLoading}
              >
                Akışı Yeniden Hesapla
              </Button>
            )}
          </div>

          {flowLoading ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "var(--m-text-subtle, #64748b)",
              }}
            >
              <FiRefreshCw
                className="spin"
                size={24}
                style={{ marginBottom: 12 }}
              />
              <div>Ürün ağacı ve hat akışı çözümleniyor...</div>
            </div>
          ) : flowStages.length === 0 ? (
            <div
              style={{
                padding: "40px 20px",
                textAlign: "center",
                color: "var(--m-text-subtle, #64748b)",
                background: "var(--m-surface-subtle, #f8fafc)",
                borderRadius: 8,
                border: "1px dashed var(--m-border, #cbd5e1)",
              }}
            >
              <FiInfo size={28} style={{ color: "#94a3b8", marginBottom: 8 }} />
              <div
                style={{
                  fontWeight: 600,
                  fontSize: 13,
                  color: "var(--m-text, #334155)",
                }}
              >
                {isEdit
                  ? "Bu iş emrine ait tanımlı aşama bulunamadı."
                  : "Ürün modeli veya parça seçildiğinde üretim hattı aşamaları otomatik olarak burada listelenecektir."}
              </div>
            </div>
          ) : (
            <div className={styles.pipelineContainer}>
              {flowStages.map((stage, idx) => {
                const isLast = idx === flowStages.length - 1;
                const hasStock =
                  (stage.currentStock || 0) >= (stage.plannedQuantity || 0);

                return (
                  <div
                    key={stage.partId || idx}
                    className={styles.pipelineStep}
                  >
                    <div
                      className={`${styles.stageCard} ${
                        stage.isFulfilledFromStock
                          ? styles.fulfilledFromStock
                          : ""
                      }`}
                    >
                      <div className={styles.stageHeader}>
                        <span className={styles.stageNumberBadge}>
                          {idx + 1}
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          {stage.isFulfilledFromStock ? (
                            <Lozenge appearance="success-subtle" isBold>
                              Stoktan Karşılanıyor
                            </Lozenge>
                          ) : stage.availableMachines?.length > 1 ? (
                            <Lozenge appearance="warning-subtle" isBold>
                              Çoklu Makine
                            </Lozenge>
                          ) : (
                            <Lozenge appearance="default" isBold>
                              Üretilecek
                            </Lozenge>
                          )}
                        </div>
                      </div>

                      <div
                        className={styles.stagePartName}
                        title={stage.partName}
                      >
                        {stage.partName || "Parça Adı Yok"}
                      </div>
                      <div className={styles.stagePartCode}>
                        Kod: {stage.partCode || "—"}
                      </div>

                      <div className={styles.stageMetrics}>
                        <div>
                          <span>Planlanan</span>
                          <strong>{stage.plannedQuantity} Adet</strong>
                        </div>
                        <div>
                          <span>Çevrim Süresi</span>
                          <strong>
                            {stage.cycleTimeSeconds
                              ? `${stage.cycleTimeSeconds} sn`
                              : "—"}
                          </strong>
                        </div>
                      </div>

                      {/* Stock Info Box */}
                      <div
                        className={`${styles.stageStockBox} ${
                          hasStock ? styles.sufficient : styles.insufficient
                        }`}
                      >
                        <div>
                          <span>Mevcut Stok:</span>
                          <strong style={{ marginLeft: 6 }}>
                            {stage.currentStock || 0} Adet
                          </strong>
                        </div>
                        {hasStock && (
                          <span style={{ fontSize: 11, fontWeight: 700 }}>
                            Yeterli
                          </span>
                        )}
                      </div>

                      {/* Stock Fulfillment Toggle (if stock is available or already fulfilled) */}
                      {(() => {
                        const stageStarted =
                          stage.status === "in_progress" ||
                          stage.status === "completed" ||
                          (Number(stage.producedQuantity) || 0) > 0;
                        const canToggle =
                          !stageStarted &&
                          ((stage.currentStock || 0) > 0 ||
                            stage.isFulfilledFromStock);

                        return (
                          <div className={styles.stockToggleContainer}>
                            {canToggle ? (
                              <label className={styles.stockToggleLabel}>
                                <input
                                  type="checkbox"
                                  checked={Boolean(stage.isFulfilledFromStock)}
                                  onChange={() => handleStockToggle(idx)}
                                />
                                <span>Stoktan Karşıla (Üretimi Atla)</span>
                              </label>
                            ) : (
                              <div className={styles.stockToggleLabelDisabled}>
                                <input
                                  type="checkbox"
                                  disabled
                                  checked={Boolean(stage.isFulfilledFromStock)}
                                />
                                <span>
                                  {stageStarted
                                    ? "Üretim Başladı (Sabit)"
                                    : "Stok Yetersiz (Üretim Zorunlu)"}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Machine and Operator Assignment Section */}
                      <div className={styles.stageAssignmentSection}>
                        {stage.isFulfilledFromStock ? (
                          <div className={styles.stockFulfilledCard}>
                            <FiCheckCircle
                              size={24}
                              className={styles.stockFulfilledIcon}
                            />
                            <div className={styles.stockFulfilledTitle}>
                              Stoktan Karşılanıyor
                            </div>
                            <div className={styles.stockFulfilledDesc}>
                              Gereken miktar mevcut stoktan karşılanacaktır.
                              Makine veya operatör ataması gerekmez.
                            </div>
                          </div>
                        ) : (
                          <>
                            {/* Machine Assignment */}
                            <div className={styles.stageMachineBox}>
                              <Label style={{ fontSize: 11, marginBottom: 4 }}>
                                {stage.availableMachines &&
                                stage.availableMachines.length > 1
                                  ? "Üretilecek Makine (Seçiniz) *"
                                  : "Atanan Makine"}
                              </Label>
                              {stage.availableMachines &&
                              stage.availableMachines.length > 1 ? (
                                <Select
                                  unique
                                  selected={
                                    stage.selectedMachineId
                                      ? [stage.selectedMachineId]
                                      : []
                                  }
                                  onSelected={(values) => {
                                    const v =
                                      values?.[0]?.value ?? values?.[0] ?? "";
                                    handleStageMachineChange(idx, v);
                                  }}
                                  value={stage.selectedMachineId}
                                  onChange={(val) =>
                                    handleStageMachineChange(
                                      idx,
                                      extractVal(val),
                                    )
                                  }
                                  options={(stage.availableMachines || [])
                                    .filter(Boolean)
                                    .map((m) => {
                                      const mId = m?._id || m;
                                      const machineObj =
                                        (typeof m === "object" && m?.name)
                                          ? m
                                          : allMachines.find(
                                              (am) =>
                                                String(am._id) === String(mId),
                                            );
                                      return {
                                        value: mId,
                                        label: `${machineObj?.name || "Makine"} (${machineObj?.code || "-"})`,
                                      };
                                    })}
                                />
                              ) : stage.availableMachines &&
                                stage.availableMachines.length === 1 ? (
                                <div className={styles.autoMachineBadge}>
                                  <FiCheck size={14} />
                                  <span>
                                    {(() => {
                                      const firstM = stage.availableMachines[0];
                                      const mId = firstM?._id || firstM;
                                      const machineObj =
                                        (typeof firstM === "object" && firstM?.name)
                                          ? firstM
                                          : allMachines.find(
                                              (am) =>
                                                String(am._id) === String(mId),
                                            );
                                      return `${machineObj?.name || "Makine"} ${
                                        machineObj?.code
                                          ? `(${machineObj.code})`
                                          : ""
                                      }`;
                                    })()}
                                  </span>
                                </div>
                              ) : (
                                <div className={styles.noMachineWarning}>
                                  <FiAlertCircle
                                    size={15}
                                    style={{ flexShrink: 0 }}
                                  />
                                  <span>
                                    Tanımlı makine yok (Parça-Makine
                                    eşleştirmesi gerekli).
                                  </span>
                                </div>
                              )}
                            </div>

                            {/* Operator Assignment */}
                            <div className={styles.stageOperatorBox}>
                              <Label style={{ fontSize: 11, marginBottom: 4 }}>
                                Atanan Operatör (Opsiyonel)
                              </Label>
                              <Select
                                unique
                                placeholder="Operatör seçin..."
                                selected={
                                  stage.selectedOperatorId
                                    ? [String(stage.selectedOperatorId)]
                                    : [""]
                                }
                                onSelected={(values) => {
                                  const v =
                                    values?.[0]?.value ?? values?.[0] ?? "";
                                  handleStageOperatorChange(idx, v);
                                }}
                                options={operatorOptions}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {!isLast && (
                      <div className={styles.pipelineArrow}>
                        <FiArrowRight />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContainer>

        {/* Bottom Save & Cancel Bar */}
        <div
          className="card card-body p-3"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 12,
            marginTop: 24,
            paddingBottom: 40,
          }}
        >
          <Button
            appearance="subtle"
            onClick={() => navigate("/work-orders")}
            disabled={saving}
          >
            İptal
          </Button>
          <Button
            appearance="primary"
            iconBefore={<FiSave size={16} />}
            type="submit"
            isLoading={saving}
          >
            {isEdit ? "Değişiklikleri Kaydet" : "İş Emrini Oluştur"}
          </Button>
        </div>
      </form>
    </AppPage>
  );
};

export default withAuth(WorkOrderFormPage);
